/**
 * Service worker registration and Web Push subscription.
 *
 * Everything here fails soft: a browser with no service worker, a user who
 * refuses notifications, and a private window that blocks storage must all land
 * on a fully working app. None of these paths may throw into a render.
 */

const SW_URL = '/sw.js'

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  if (import.meta.env.DEV) return null

  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: '/' })
  } catch {
    return null
  }
}

/**
 * The VAPID key arrives base64url-encoded; push wants raw bytes.
 *
 * The buffer is allocated explicitly so the result is a `Uint8Array<ArrayBuffer>`
 * rather than the `ArrayBufferLike` a bare `new Uint8Array(length)` infers —
 * `applicationServerKey` rejects the latter because it could be shared memory.
 */
function decodeVapidKey(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalised)
  const buffer = new ArrayBuffer(raw.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)
  return bytes
}

export interface PushSubscriptionPayload {
  endpoint: string
  p256dh: string
  auth: string
}

function encodeKey(subscription: PushSubscription, name: 'p256dh' | 'auth'): string {
  const key = subscription.getKey(name)
  if (!key) return ''
  return btoa(String.fromCharCode(...new Uint8Array(key)))
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * Asks for permission and subscribes. Returns null whenever push is not
 * available or the user declined — the caller treats that as "push is off",
 * never as an error.
 */
export async function subscribeToPush(
  vapidPublicKey: string,
): Promise<PushSubscriptionPayload | null> {
  if (!pushSupported() || vapidPublicKey === '') return null

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return null

    const registration = await navigator.serviceWorker.ready
    const existing = await registration.pushManager.getSubscription()
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decodeVapidKey(vapidPublicKey),
      }))

    return {
      endpoint: subscription.endpoint,
      p256dh: encodeKey(subscription, 'p256dh'),
      auth: encodeKey(subscription, 'auth'),
    }
  } catch {
    return null
  }
}

/** Drops the browser subscription and returns its endpoint so the server can forget it too. */
export async function unsubscribeFromPush(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    const endpoint = subscription?.endpoint ?? null
    await subscription?.unsubscribe()
    return endpoint
  } catch {
    // Nothing to undo.
    return null
  }
}
